// NAMES — batch 5's rebuild of AttachNames (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on).
//
// ONE UNRESOLVED NAME AT A TIME (Astra, names): search first, then ONE register of the names still unresolved — the
// name as legacy typed it, its trades, the suggested party, its state — and a name opens in the detail view (?name= in
// the URL, so Back returns to the list), with its history (trades, volume, first and last seen) and the one choice:
// who it is (Attach) or that it is not a name. Attaching moves on to the next unresolved name. The names already
// decided are behind one quiet link under the list.
//
// THE READ AND THE WRITE ARE TODAY'S (screens/AttachNames.tsx): ['unnamed'] and api.attachName with the same body; the
// list is read again 1.2 s after every write (Hyperdrive's cache can answer the pre-write list straight after one);
// the suggestion is the server's (an exact or uniquely case-insensitive match — never a substring); Attach needs a party
// picked or suggested; a refusal stays with the name it refused, and is said at the top when that name is not open;
// a failed refresh keeps the queue and says so; "every trade is named" needs a read that succeeded.
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { api, type UnnamedGroup, type UnnamedResponse } from '../../lib/api';
import { fmtAmount, fmtDay } from '../../lib/format';
import { partyName } from '../../lib/naming';
import { LAST_READ_NONE, ReadFailed, Refused } from '../../components/OnboardingParts';
import { Pill } from '../../components/Pill';
import {
  DetailSheet, EmptyState, Fact, Notice, PageHead, Panel, Register, SearchBar, SkeletonRows,
  type RegisterColumn, ReadNotice } from '../../components/v2/kit';

const since = (iso: string | null): string => (iso ? fmtDay(iso) : '—');

export function NamesV2() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [showDone, setShowDone] = useState(false);
  const needle = search.trim().toLowerCase();

  const { data, isPending, error, refetch } = useQuery<UnnamedResponse>({
    queryKey: ['unnamed'],
    queryFn: () => api.unnamedNames(),
  });

  const attach = useMutation({
    mutationFn: (v: { name_key: string; name_seen: string; party_slug?: string; ignore?: boolean }) =>
      api.attachName(v),
    onSettled: () => {
      setBusy(null);
      // The list is served through Hyperdrive, which caches reads — so a refetch straight after a write can return the
      // PRE-write list. The row disappearing a beat late is the cache, not a failed save.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['unnamed'] }), 1200);
    },
  });

  const parties = data?.parties;
  const nameOf = useCallback((slug: string | null | undefined) => {
    if (!slug) return null;
    const p = parties?.find((x) => x.slug === slug);
    return p ? partyName(p) : partyName({ slug });
  }, [parties]);

  const open = useMemo(() => (data?.groups ?? []).filter((g) => !g.resolved && !g.ignored), [data]);
  const done = useMemo(() => (data?.groups ?? []).filter((g) => g.resolved || g.ignored), [data]);
  const shown = useMemo(() => (needle
    ? open.filter((g) => `${g.name_seen} ${nameOf(g.suggestion?.slug) ?? ''}`.toLowerCase().includes(needle))
    : open), [open, needle, nameOf]);

  const openKey = params.get('name');
  const selected = open.find((g) => g.name_key === openKey) ?? null;
  const show = useCallback((key: string | null, replace = false) => setParams((x) => {
    const n = new URLSearchParams(x);
    if (key == null) n.delete('name'); else n.set('name', key);
    return n;
  }, { replace }), [setParams]);
  const close = useCallback(() => show(null, true), [show]);

  // A REFUSAL BELONGS TO THE NAME IT REFUSED: in its detail view while that name is open; at the top otherwise.
  const failedKey = attach.isError ? attach.variables?.name_key ?? null : null;

  /** AttachNames.tsx `run`, verbatim — then, once the books said yes, the next unresolved name opens (or the view
   *  closes when it was the last). */
  const run = (g: UnnamedGroup, ignore: boolean) => {
    const slug = picked[g.name_key] ?? g.suggestion?.slug;
    if (!ignore && !slug) return;
    setBusy(g.name_key);
    const i = shown.findIndex((x) => x.name_key === g.name_key);
    const next = shown.slice(i + 1).find((x) => x.name_key !== g.name_key) ?? shown.find((x) => x.name_key !== g.name_key) ?? null;
    attach.mutate(
      { name_key: g.name_key, name_seen: g.name_seen, party_slug: ignore ? undefined : slug, ignore },
      { onSuccess: () => show(next?.name_key ?? null, true) },
    );
  };

  const head = <PageHead title="Names" />;

  // PENDING, not "loading": a first read opened with no connection is paused and waits.
  if (isPending) return <>{head}<Panel flush><SkeletonRows what="the names" rows={3} /></Panel></>;
  // A failed read with nothing to show; a failed refresh keeps the queue (the screen reads again after every write, and a
  // blip must not lose his place mid-pass).
  if (error && !data) {
    return <>{head}<ReadNotice what="the names" error={error} onRetry={() => { void refetch(); }} /></>;
  }
  if (!data) return <>{head}<Panel flush><SkeletonRows what="the names" rows={3} /></Panel></>;

  const columns: ReadonlyArray<RegisterColumn<UnnamedGroup>> = [
    { key: 'name', head: 'Unresolved name', cell: (g) => (
      <span className="block min-w-0">
        <span className="block break-words">{g.name_seen}</span>
        <span className="mt-1 block text-meta text-tg-hint">Legacy alias</span>
      </span>
    ) },
    { key: 'trades', head: 'Trades', align: 'right', width: 'w-[12%]', cell: (g) => <span className="amt">{g.deals}</span> },
    { key: 'candidate', head: 'Suggested', align: 'right', width: 'w-[26%]', cell: (g) => nameOf(g.suggestion?.slug) ?? <span className="text-tg-hint">—</span> },
    { key: 'state', head: 'State', align: 'right', width: 'w-[16%]', cell: () => <span className="inline-flex items-center gap-3"><Pill tone="warn">Unresolved</Pill><ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden /></span> },
  ];

  const slug = selected ? picked[selected.name_key] ?? selected.suggestion?.slug ?? '' : '';

  return (
    <>
      {head}
      {error && (
        <Notice tone="warn">
          <ReadFailed what="the names" error={error} says={open.length === 0 ? LAST_READ_NONE : 'showing the last good read'} retry={() => { void refetch(); }} />
        </Notice>
      )}
      {attach.isError && failedKey !== selected?.name_key && <Refused error={attach.error} className="mb-4" />}

      <SearchBar value={search} onChange={setSearch} placeholder="Search a name" />

      <Panel flush>
        {shown.length > 0 && (
          <Register label="Unresolved names" columns={columns} rows={shown} rowKey={(g) => g.name_key} onOpen={(g) => show(g.name_key)}
            card={(g) => ({
              title: g.name_seen,
              figure: <span className="text-meta text-tg-hint"><span className="amt text-tg-text">{g.deals}</span> {Number(g.deals) === 1 ? 'trade' : 'trades'}</span>,
              meta: g.suggestion ? `Suggested · ${nameOf(g.suggestion.slug)}` : 'Legacy alias',
              state: <Pill tone="warn">Unresolved</Pill>,
            })} />
        )}
        {/* "Every trade is named" is an all-clear: never over a refresh that failed. */}
        {shown.length === 0 && !error && (
          <EmptyState className="px-4 lg:py-16" action={needle ? <button type="button" className="btn-link" onClick={() => setSearch('')}>Clear search</button> : undefined}>
            {needle ? 'Nothing matches this search.' : 'Every trade is named.'}
          </EmptyState>
        )}
        {shown.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span>{[!error && `${shown.length} unresolved`, 'Most trades first'].filter(Boolean).join(' · ')}</span>
          </div>
        )}
      </Panel>

      {/* THE NAMES ALREADY DECIDED — behind one quiet link: the name, and the party it went to or "not a name". */}
      {done.length > 0 && (
        <div className="mt-4">
          <button type="button" aria-expanded={showDone} onClick={() => setShowDone((v) => !v)}
            className="flex min-h-11 items-center gap-1.5 text-body text-tg-hint hover:text-tg-text lg:min-h-10">
            <ChevronRight className={`h-4 w-4 transition-transform ${showDone ? 'rotate-90' : ''}`} aria-hidden />{done.length} already decided
          </button>
          {showDone && (
            <ul className="mt-1">
              {done.map((g) => (
                <li key={g.name_key} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-stroke-soft py-2 text-body last:border-b-0">
                  <span className="min-w-0 break-words">{g.name_seen}</span>
                  <span className="shrink-0 text-meta text-tg-hint">
                    {g.ignored ? 'not a name' : `→ ${nameOf(g.attached_slug) ?? '—'}`} · <span className="amt">{g.deals}</span> {Number(g.deals) === 1 ? 'trade' : 'trades'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {selected && (
        <DetailSheet
          title={<span className="break-words">{selected.name_seen}</span>}
          onClose={close}
          footer={
            <div className="grid grid-cols-2 gap-2">
              <button type="button" className="btn-primary px-4 text-body" disabled={!slug || busy === selected.name_key} onClick={() => run(selected, false)}>
                {busy === selected.name_key && !attach.variables?.ignore ? 'Attaching…' : 'Attach'}
              </button>
              <button type="button" className="btn-secondary px-4 text-body" disabled={busy === selected.name_key} onClick={() => run(selected, true)}
                title="Not a counterparty — a note, a reference, or something that should never resolve">
                Not a name
              </button>
            </div>
          }
        >
          <div>
            <Fact label="Trades"><span className="amt">{selected.deals}</span></Fact>
            {selected.volume_quote && <Fact label="Volume"><span className="amt">{fmtAmount(selected.volume_quote, selected.quote_ccy ?? undefined)}</span> {selected.quote_ccy}</Fact>}
            <Fact label="Seen">{since(selected.first_seen)} – {since(selected.last_seen)}</Fact>
            {selected.suggestion && <Fact label="Suggested">{nameOf(selected.suggestion.slug)}</Fact>}
          </div>
          <label className="mt-6 block">
            <span className="mb-1 block text-meta text-tg-hint">Who is this?</span>
            <select className="select w-full" value={slug} onChange={(e) => setPicked({ ...picked, [selected.name_key]: e.target.value })}>
              <option value="">Pick a party</option>
              {data.parties.map((p) => (
                <option key={p.slug} value={p.slug}>{partyName(p)} · {p.party_type === 'counterparty' ? 'merchant' : 'customer'}</option>
              ))}
            </select>
          </label>
          {failedKey === selected.name_key && <Refused error={attach.error} className="mt-4" />}
        </DetailSheet>
      )}
    </>
  );
}
